import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Heart, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const { user, setUser } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          const me = await api.get("/auth/me");
          setUser(me.data);
          return;
        }
      } catch {}
      tries += 1;
      if (tries < 20) timer = setTimeout(poll, 2000);
      else setStatus("error");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="relative flex min-h-[calc(100vh-64px)] items-center justify-center overflow-hidden px-4 py-16" data-testid="payment-success-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream/50 to-cream" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-gold/50 bg-white/90 p-10 text-center shadow-card backdrop-blur-md"
      >
        {status === "pending" && (
          <>
            <Loader2 size={36} className="mx-auto animate-spin text-terra" />
            <h1 className="mt-5 font-serif text-2xl font-medium text-ink">Confirmation du paiement…</h1>
            <p className="mt-2 text-sm text-clay">Quelques secondes suffisent.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-light">
              <Check size={30} className="text-terra" />
            </div>
            {user?.archetype && !user?.retake_credit ? (
              <>
                <h1 className="mt-5 font-serif text-2xl font-medium text-ink" data-testid="payment-success-title">Votre accès est prolongé !</h1>
                <p className="mt-2 text-sm leading-relaxed text-clay">Un nouveau mois de rencontres s'ouvre pour vous — vos réponses sont restées enregistrées, rien à refaire.</p>
                <Link to="/discover" data-testid="payment-success-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:scale-[1.03] hover:bg-terra-dark">
                  Découvrir mes nouveaux matchs <Heart size={15} fill="currentColor" />
                </Link>
              </>
            ) : (
              <>
                <h1 className="mt-5 font-serif text-2xl font-medium text-ink" data-testid="payment-success-title">Paiement confirmé !</h1>
                <p className="mt-2 text-sm leading-relaxed text-clay">Votre test de compatibilité est débloqué. Les cartes vous attendent.</p>
                <Link to="/quiz" data-testid="payment-success-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark">
                  Commencer le test <Heart size={15} fill="currentColor" />
                </Link>
              </>
            )}
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-serif text-2xl font-medium text-ink">Vérification impossible</h1>
            <p className="mt-2 text-sm text-clay">Le paiement n'a pas pu être confirmé. Si vous avez été débité, contactez-nous via « Vos avis ».</p>
            <Link to="/quiz" data-testid="payment-retry-link" className="mt-6 inline-block rounded-full bg-terra px-7 py-3.5 text-sm font-semibold text-cream shadow-boho hover:bg-terra-dark">
              Retour au test
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
